import './App.scss';
import {BrowserRouter as Router, Navigate, Outlet, Route, Routes as Switch} from 'react-router-dom';
import {ToastContainer} from "react-toastify";
import {Layout} from "components";
import {useSelector} from "react-redux";

import 'react-toastify/dist/ReactToastify.css';
import {globals, routes, utils} from 'common';
import {ADMIN_PERMISSIONS, GROUND_ADMIN_PERMISSIONS, ROLES} from 'common/constants';
import {
  Analytics,
  AdminWithdrawals,
  Bookings,
  Earnings,
  Grounds,
  Login,
  Notifications,
  OfflineBooking,
  Profile, Reports, ReportDetail,
  ResetPassword, RoleCreate, RoleDetail, Roles,
  Fields, FieldCreate, FieldDetail, Users, UserCreate, UserDetail,
  Withdraw, GroundDetail, GroundCreate,
  BookingDetail, OfflineBookingCreate,
  Maintenance, MaintenanceCreate,
  Calendar
} from 'pages';

const RequireAuth = () => {
  const isUserLoggedIn = useSelector(state => state.auth.isUserLoggedIn);

  return (
    isUserLoggedIn ? <Outlet/> : <Navigate to={routes.LOGIN}/>
  );
};

const RequireAdmin = () => {
  const user = useSelector(state => state.auth.user);
  return user?.role === ROLES.ADMIN ? <Outlet/> : <Navigate to={routes.GROUNDS}/>;
};

const RequireGroundOwner = () => {
  const user = useSelector(state => state.auth.user);
  return user?.role === ROLES.GROUND_ADMIN ? <Outlet/> : <Navigate to={routes.USERS}/>;
};

const RequirePermission = ({ permission, redirectTo = routes.GROUNDS }) => {
  const user = useSelector(state => state.auth.user);
  return utils.hasPermission(user, permission) ? <Outlet /> : <Navigate to={redirectTo} />;
};

function App() {
  return (
    <div className='app'>
      <Router>
        <Switch>
          <Route path={routes.LOGIN} element={<Login/>}/>
          <Route path='/' element={<Layout/>}>
            <Route element={<RequireAuth/>}>
              <Route path={routes.PROFILE} element={<Profile/>}/>
              <Route path={routes.CHANGE_PASSWORD} element={<ResetPassword/>}/>
              {/* Ground Admin Permission-based routes */}
              <Route element={<RequireGroundOwner/>}>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.GROUNDS} />}>
                  <Route path={routes.GROUNDS} element={<Grounds/>}/>
                  <Route path={routes.GROUND_DETAIL} element={<GroundDetail/>}/>
                  <Route path={routes.GROUND_CREATE} element={<GroundCreate/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.SLOTS} />}>
                  <Route path={routes.SLOTS} element={<Fields/>}/>
                  <Route path={routes.SLOT_CREATE} element={<FieldCreate/>}/>
                  <Route path={routes.SLOT_DETAIL} element={<FieldDetail/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.BOOKINGS} />}>
                  <Route path={routes.BOOKINGS} element={<Bookings/>}/>
                  <Route path={routes.BOOKING_DETAIL} element={<BookingDetail/>}/>
                  <Route path={routes.CALENDAR} element={<Calendar/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.EARNING} />}>
                  <Route path={routes.EARNINGS} element={<Earnings/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.WITHDRAW} />}>
                  <Route path={routes.WITHDRAW_REQUEST} element={<Withdraw/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.NOTIFICATIONS} />}>
                  <Route path={routes.NOTIFICATIONS} element={<Notifications/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.OFFLINE_BOOKINGS} />}>
                  <Route path={routes.OFFLINE_BOOKING} element={<OfflineBooking/>}/>
                  <Route path={routes.OFFLINE_BOOKING_CREATE} element={<OfflineBookingCreate/>}/>
                </Route>
                <Route element={<RequirePermission permission={GROUND_ADMIN_PERMISSIONS.MAINTENANCE} />}>
                  <Route path={routes.MAINTENANCE} element={<Maintenance/>}/>
                  <Route path={routes.MAINTENANCE_CREATE} element={<MaintenanceCreate/>}/>
                  <Route path="/maintenance/:id" element={<MaintenanceCreate/>}/>
                </Route>
              </Route>
              <Route element={<RequireAdmin/>}>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.USERS} />}>
                  <Route path={routes.USERS} element={<Users/>}/>
                  <Route path={routes.USER_CREATE} element={<UserCreate/>}/>
                  <Route path={routes.USER_DETAIL} element={<UserDetail/>}/>
                </Route>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.ROLES} />}>
                  <Route path={routes.ROLES} element={<Roles/>}/>
                  <Route path={routes.ROLE_CREATE} element={<RoleCreate/>}/>
                  <Route path={routes.ROLE_DETAIL} element={<RoleDetail/>}/>
                </Route>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.APPROVALS} />}>
                  <Route path={routes.APPROVAL} element={<Grounds approvalsMode={true} />}/>
                  <Route path="/approval/:id" element={<GroundDetail approvalMode={true} />} />
                </Route>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.REPORTS} />}>
                  <Route path={routes.REPORTS} element={<Reports/>}/>
                  <Route path={routes.REPORT_DETAIL} element={<ReportDetail/>}/>
                </Route>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.ANALYTICS} />}>
                  <Route path={routes.ANALYTICS} element={<Analytics/>}/>
                </Route>
                <Route element={<RequirePermission permission={ADMIN_PERMISSIONS.WITHDRAWALS} />}>
                  <Route path={routes.ADMIN_WITHDRAWALS} element={<AdminWithdrawals/>}/>
                </Route>
              </Route>
              <Route path='*' element={<Navigate to={routes.GROUNDS}/>}/>
            </Route>
          </Route>
        </Switch>
      </Router>
      <ToastContainer autoClose={globals.TOAST_TIMER}/>
    </div>
  );
}

export default App;
